import {createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {RequestStatus} from "../../../../api/apiConfiguration";
import {RootState} from "../../../../app/store";
import {
    AssistantCloneResult,
    AssistantImportResult,
    CloneAssistantRequest,
    cloneAssistantConfiguration,
    exportAssistantConfiguration,
    importAssistantConfiguration,
    saveExportedFile
} from "./assistantTransferApi";

interface AssistantTransferState {
    exportRequestStatus: RequestStatus;
    importRequestStatus: RequestStatus;
    importError?: string;
    importResult?: AssistantImportResult;
    cloneRequestStatus: RequestStatus;
    cloneError?: string;
}

const initialState: AssistantTransferState = {
    exportRequestStatus: RequestStatus.NONE,
    importRequestStatus: RequestStatus.NONE,
    cloneRequestStatus: RequestStatus.NONE
};

/**
 * Downloads the assistant's configuration and hands the archive to the browser.
 *
 * Resolves with the file name so the caller can confirm what was downloaded.
 */
export const exportAssistant = createAsyncThunk<string, string, { rejectValue: string }>(
    "assistantTransfer/export",
    async (publicBotId, {rejectWithValue}) => {
        try {
            const file = await exportAssistantConfiguration(publicBotId);
            saveExportedFile(file);
            return file.fileName;
        } catch (error: any) {
            return rejectWithValue(error?.message ?? "Failed to export the assistant configuration");
        }
    }
);

/**
 * Replaces the assistant's configuration with the contents of an exported archive.
 */
export const importAssistant = createAsyncThunk<AssistantImportResult,
    { publicBotId: string, file: File },
    { rejectValue: string }>(
    "assistantTransfer/import",
    async ({publicBotId, file}, {rejectWithValue}) => {
        try {
            return await importAssistantConfiguration(publicBotId, file);
        } catch (error: any) {
            return rejectWithValue(error?.message ?? "Failed to import the assistant configuration");
        }
    }
);

/**
 * Creates a copy of the assistant, optionally duplicating its franchises and locations.
 */
export const cloneAssistant = createAsyncThunk<AssistantCloneResult,
    { publicBotId: string, request: CloneAssistantRequest },
    { rejectValue: string }>(
    "assistantTransfer/clone",
    async ({publicBotId, request}, {rejectWithValue}) => {
        try {
            return await cloneAssistantConfiguration(publicBotId, request);
        } catch (error: any) {
            return rejectWithValue(error?.message ?? "Failed to clone the assistant");
        }
    }
);

export const assistantTransferSlice = createSlice({
    name: "assistantTransfer",
    initialState,
    reducers: {
        resetImportState: (state) => {
            state.importRequestStatus = RequestStatus.NONE;
            state.importError = undefined;
            state.importResult = undefined;
        },
        resetCloneState: (state) => {
            state.cloneRequestStatus = RequestStatus.NONE;
            state.cloneError = undefined;
        }
    },
    extraReducers: builder => {
        builder.addCase(exportAssistant.pending, (state) => {
            state.exportRequestStatus = RequestStatus.PENDING;
        });
        builder.addCase(exportAssistant.fulfilled, (state) => {
            state.exportRequestStatus = RequestStatus.RESOLVED;
        });
        builder.addCase(exportAssistant.rejected, (state) => {
            state.exportRequestStatus = RequestStatus.REJECTED;
        });

        builder.addCase(importAssistant.pending, (state) => {
            state.importRequestStatus = RequestStatus.PENDING;
            state.importError = undefined;
            state.importResult = undefined;
        });
        builder.addCase(importAssistant.fulfilled, (state, {payload}) => {
            state.importRequestStatus = RequestStatus.RESOLVED;
            state.importResult = payload;
        });
        builder.addCase(importAssistant.rejected, (state, {payload}) => {
            state.importRequestStatus = RequestStatus.REJECTED;
            state.importError = payload ?? "Failed to import the assistant configuration";
        });

        builder.addCase(cloneAssistant.pending, (state) => {
            state.cloneRequestStatus = RequestStatus.PENDING;
            state.cloneError = undefined;
        });
        builder.addCase(cloneAssistant.fulfilled, (state) => {
            state.cloneRequestStatus = RequestStatus.RESOLVED;
        });
        builder.addCase(cloneAssistant.rejected, (state, {payload}) => {
            state.cloneRequestStatus = RequestStatus.REJECTED;
            state.cloneError = payload ?? "Failed to clone the assistant";
        });
    }
});

export const {resetImportState, resetCloneState} = assistantTransferSlice.actions;

export const exportRequestStatusSelector = (state: RootState): RequestStatus =>
    state.assistantTransfer.exportRequestStatus;
export const importRequestStatusSelector = (state: RootState): RequestStatus =>
    state.assistantTransfer.importRequestStatus;
export const importErrorSelector = (state: RootState): string | undefined =>
    state.assistantTransfer.importError;
export const importResultSelector = (state: RootState): AssistantImportResult | undefined =>
    state.assistantTransfer.importResult;
export const cloneRequestStatusSelector = (state: RootState): RequestStatus =>
    state.assistantTransfer.cloneRequestStatus;
export const cloneErrorSelector = (state: RootState): string | undefined =>
    state.assistantTransfer.cloneError;
